import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@common-fe/common-fe';
import { Option, OptionKey } from '@common-fe/common-fe';
import { AxiosResponse } from 'axios';
import _ from 'lodash';

import { api } from '@/api';
import { Pagination, PaginationParams } from '@/common/types';
import {
  API_URL,
  ICustomPageOrganizationShortDto,
  ISearchOrganizationDto,
  ISearchOrganizationDtoOrganizationTypesEnum,
  ISearchOrganizationDtoStatusesEnum,
  ISearchOrganizationDtoTypeEnum,
} from '@/dtos';
import { RoleLevel } from '@/modules/role/role.types';

interface Params extends PaginationParams {
  organizationId?: OptionKey;
}
interface CallParams {
  partners: OptionKey[];
}
const useRoleLevelQuery = ({ perPage, searchString, organizationId }: Params) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [currentItems, setCurrentItems] = useState<Option[]>([]);
  const { isError, isSuccess, data, isLoading, mutateAsync } = useMutation(
    (callParams?: CallParams) =>
      api.post<
        Pagination<ICustomPageOrganizationShortDto>,
        AxiosResponse<ICustomPageOrganizationShortDto>,
        ISearchOrganizationDto
      >(
        API_URL.ORGANIZATIONS_SHORT,
        {
          organization_types: [
            RoleLevel.Partner,
          ] as unknown as Set<ISearchOrganizationDtoOrganizationTypesEnum>,
          ...(organizationId !== undefined && organizationId !== null && organizationId !== ''
            ? { partner_id: _.toNumber(organizationId) }
            : {}),
          // TODO inconsistent type
          // @ts-ignore
          organization_ids: callParams?.partners?.length ? undefined : callParams?.partners.join(','),
          type: [RoleLevel.Partner] as unknown as Set<ISearchOrganizationDtoTypeEnum>,
          statuses: [
            ISearchOrganizationDtoStatusesEnum.ACTIVE,
          ] as unknown as Set<ISearchOrganizationDtoStatusesEnum>,
          search_value: searchString,
        },
        {
          params: _.pickBy({
            size: perPage,
            page: currentPage,
          }),
        }
      ),
    {
      onSuccess: (data: AxiosResponse<ICustomPageOrganizationShortDto>) => {
        const list = data?.data?.content || [];

        const formattedList = list.map((item) => {
          const id = `${item.id}`;
          return {
            key: id,
            title: item.name,
            value: item.name,
          };
        });
        const newList = _.uniqBy([...currentItems, ...formattedList], 'key');

        setCurrentItems(newList);
      },
      onError: () => {},
    }
  );
  const handleReset = useCallback(() => {
    setCurrentPage(0);
    setCurrentItems([]);
  }, []);

  useEffect(() => {
    setCurrentItems([]);
    setCurrentPage(0);
  }, [searchString]);
  // @ts-ignore
  const totalPages = useMemo(() => data?.data?.totalPages || 1, [data?.data?.totalPages]);
  const handleLoadMore = useCallback(() => {
    const newPage = currentPage + 1;
    if (newPage < totalPages) {
      setCurrentPage(newPage);
      mutateAsync(undefined);
    }
  }, [currentPage, mutateAsync, totalPages]);

  const handleSync = useCallback(
    async (callParams?: CallParams) => {
      await mutateAsync(callParams);
    },
    [mutateAsync]
  );

  return {
    options: currentItems,
    handleSync,
    isLoading,
    isSuccess,
    isError,
    handleLoadMore,
    handleReset,
  };
};
export default useRoleLevelQuery;
